import type { ConsolidatedExtractionResult } from '@avisoma/shared';

/**
 * Render a one-line summary of `consolidatedExtraction` for the list-row
 * Extraction cell. Returns `"—"` when null or every per-type array is empty
 * (the entity hasn't been extracted yet, or extraction produced nothing).
 *
 * Multi-type case: comma-joined in display order (AD → SB → LLP → Part Movement →
 * Engine → LDND), matching the order on the detail page's Consolidated
 * Extraction card.
 */
export function summariseExtraction(extraction: ConsolidatedExtractionResult | null): string {
  if (!extraction) return '—';

  const parts: string[] = [];
  const adCount = extraction.adStatusEvents?.length ?? 0;
  const sbCount = extraction.sbStatusEvents?.length ?? 0;
  const llpCount = extraction.llpStatusEvents?.length ?? 0;
  const pmCount = extraction.partMovementEvents?.length ?? 0;
  const engCount = extraction.engineEvents?.length ?? 0;
  const ldndCount = extraction.ldndStatusEvents?.length ?? 0;
  const arcCount = extraction.arcEvents?.length ?? 0;
  const qecCount = extraction.qecListEvents?.length ?? 0;
  const bladeCount = extraction.bladeStatusEvents?.length ?? 0;
  const workscopeCount = extraction.workscopeEvents?.length ?? 0;
  const borescopeCount = extraction.borescopeInspections?.length ?? 0;
  const repairCount = extraction.repairRecords?.length ?? 0;
  const nisCount = extraction.nisStatementEvents?.length ?? 0;
  const thrustCount = extraction.thrustConversionEvents?.length ?? 0;
  const movementCount = extraction.engineMovementEvents?.length ?? 0;
  const pmaDerCount =
    (extraction.pmaDerStatus?.declarations?.length ?? 0) +
    (extraction.pmaDerStatus?.parts?.length ?? 0);

  if (adCount > 0) parts.push(`${adCount} AD event${adCount === 1 ? '' : 's'}`);
  if (sbCount > 0) parts.push(`${sbCount} SB event${sbCount === 1 ? '' : 's'}`);
  if (llpCount > 0) parts.push(`${llpCount} LLP event${llpCount === 1 ? '' : 's'}`);
  if (pmCount > 0) parts.push(`${pmCount} part movement${pmCount === 1 ? '' : 's'}`);
  if (engCount > 0) parts.push(`${engCount} engine event${engCount === 1 ? '' : 's'}`);
  if (ldndCount > 0) parts.push(`${ldndCount} LDND event${ldndCount === 1 ? '' : 's'}`);
  if (arcCount > 0) parts.push(`${arcCount} ARC event${arcCount === 1 ? '' : 's'}`);
  if (qecCount > 0) parts.push(`${qecCount} QEC row${qecCount === 1 ? '' : 's'}`);
  if (bladeCount > 0) parts.push(`${bladeCount} blade/nozzle row${bladeCount === 1 ? '' : 's'}`);
  if (workscopeCount > 0)
    parts.push(`${workscopeCount} workscope event${workscopeCount === 1 ? '' : 's'}`);
  if (borescopeCount > 0) parts.push(`${borescopeCount} inspection${borescopeCount === 1 ? '' : 's'}`);
  if (repairCount > 0) parts.push(`${repairCount} repair${repairCount === 1 ? '' : 's'}`);
  if (nisCount > 0) parts.push(`${nisCount} NIS statement${nisCount === 1 ? '' : 's'}`);
  if (thrustCount > 0) parts.push(`${thrustCount} thrust change${thrustCount === 1 ? '' : 's'}`);
  if (movementCount > 0)
    parts.push(`${movementCount} engine movement${movementCount === 1 ? '' : 's'}`);
  if (pmaDerCount > 0) parts.push(`${pmaDerCount} PMA/DER row${pmaDerCount === 1 ? '' : 's'}`);
  else if (extraction.pmaDerStatus) parts.push('PMA/DER statement');
  const nonExceedanceCount = extraction.nonExceedanceStatement?.declarations?.length ?? 0;
  if (nonExceedanceCount > 0)
    parts.push(`${nonExceedanceCount} non-exceedance declaration${nonExceedanceCount === 1 ? '' : 's'}`);
  if (extraction.utilisationRecord) {
    const lastFlight = extraction.utilisationRecord.latestFlightDate;
    parts.push(lastFlight ? `Utilisation: last flight ${lastFlight}` : 'Utilisation record');
  }

  return parts.length > 0 ? parts.join(', ') : '—';
}
